import React, { Fragment, useEffect, useState } from 'react';
import { Alert, Button, Card, CardBody, CardHeader, CardTitle, Col, Container, FormGroup, Label, Row, Table } from 'reactstrap';

import PropControl from '../base/components/PropControl';
import DataStore from '../base/plumbing/DataStore';
import ServerIO from '../plumbing/ServerIO';

import BlindUpload from '../base/components/BlindUpload';
import { A } from '../base/plumbing/glrouter';

import Misc from './MiscOverrides';
import { getDataItem } from '../base/plumbing/Crud';
import C from '../C';
import { savedManifestPath, shortenName, startAnalysis, receiveUploadAnalysis, fetchSavedManifest } from '../base/components/creative-recommendations/recommendation-utils';
import { flattenProp, transferTotal } from '../base/utils/pageAnalysisUtils';
import { FrameNavigator, FriendlyURL, SubFrameInfo, TypeBreakdown } from '../base/components/PageRecommendations';
import { bytes } from '../base/utils/miscutils';
import CopyButton from '../base/components/CopyButton';

import '../../style/MeasurePage.less';

const path = ['widget', 'measure'];


/** Nice display of total data transferred under a PageManifest or Transfer. */
function TransferBytes({transfer, copy}) {
	const _bytes = transferTotal(transfer);
	return <span title={`${_bytes} bytes`}>{bytes(_bytes)}{copy && <CopyButton size="xs" text={_bytes} className="ml-2" />}</span>;
}


/** MIME types, URLs, sizes for all transfers in the page */
function TransferTable({manifest, separateSubframes}) {
	const [transfers, setTransfers] = useState([]);
	if (!manifest) return null;

	useEffect(() => {
		let nextTransfers;
		if (separateSubframes) {
			nextTransfers = [...manifest.transfers];
		} else {
			nextTransfers = flattenProp(manifest, 'transfers', 'frames');
		}
		nextTransfers.sort((a, b) => (transferTotal(b) - transferTotal(a)));

		setTransfers(nextTransfers);
	}, [manifest, separateSubframes]);

	// Account for data in sub-frames - subtract totals of each frame from total of root manifest
	const subFrameBytes = separateSubframes ? manifest.frames.reduce((acc, f) => (acc - transferTotal(f)), transferTotal(manifest)) : 0;
	const subFramesRow = subFrameBytes ? <tr key="subframes">
		<td>{subFrameBytes}</td>
		<td /><td />
		<td>Content in sub-frames</td>
		{/*<td />*/}
	</tr> : null;

	return <Row className="transfers">
		<Col xs="12">
			<h4>Transfers:</h4>
			<Table size="sm">
				<thead>
					<tr><th>Bytes</th><th>Type</th><th>Domain</th><th>File</th>{/*<th>Foreign AdTech</th>*/}</tr>
				</thead>
				<tbody>
					{transfers.map(t => <tr key={`${t.url}:${t.timestamp}`}>
						<td><TransferBytes transfer={t} /></td>
						<td>{t.mimeType}</td>
						<td>{new URL(t.url).hostname}</td>
						<td><A href={t.url} target="_blank">{shortenName(t.url)}</A></td>
						{/*<td>{t.extra?.adtech && t.extra?.foreign}</td>*/}
					</tr>)}
					{subFramesRow}
				</tbody>
			</Table>
		</Col>
	</Row>;
}


/** Matches the domain and path prefix for a resource which has been extracted from a ZIP file by MeasureServlet */
const unzippedFileRegex = /.+\.good-loop\.com\/measure-files\/unzipped\/[^/]+\//;

/** Key facts about a page - screenshot, URL, transfer totals */
function PageOverview({manifest}) {
	if (!manifest) return null;
	const { url, width, height } = manifest;
	// Manageable name for the page URL
	const [shortName, setShortName] = useState('');
	useEffect(() => {
		// For creatives uploaded as ZIP files - strip the "unzipped here" part of the URL
		const internalPath = url.replace(unzippedFileRegex, '');
		// For all others, just use the filename part
		setShortName((internalPath !== url) ? internalPath : shortenName(url));
	}, [manifest.url]);

	return <Col xs="12" sm="6">
		<img src={manifest.screenshot} className="manifest-screenshot img-thumbnail pull-left p-0 mr-2" alt={`Screenshot of ${url}`} />
		<div>
			<A href={url} title={url}>{shortName}</A>
			{manifest.parentFrame ? ' (Sub-frame)' : ' (Page root)'}
		</div>
		<div>Size: {width} x {height}</div>
		<div>Total data transfer: <strong><TransferBytes transfer={manifest} copy /></strong></div>
	</Col>;
}


/**
 * Quick indicator of your location in the frame hierarchy.
 * Buttons for root, current, ancestors in between, and indication
 * of how many sub-frames are children of the current focus.
 * @param {object} p
 * @param {PageManifest} p.frame The currently-focused frame.
 * @param {function} p.setFrame Called to navigate to another frame.
 * @returns {JSX.Element[]}
 */
function FrameBreadcrumbs({frame, setFrame}) {
	const elements = [];
	let count = frame.frames.length;
	const s = (count === 1) ? '' : 's';
	if (!count) count = 'No';
	elements.push(` ⟶ ${count} Sub-Frame${s}`);

	let cursor = frame;
	let i = 0;
	while (cursor) {
		const isCurrent = (cursor === frame);
		const isRoot = !cursor.parentFrame;
		const clickTarget = cursor; // Don't try to optimise this out! Reassignment of outer-scope `cursor` means it always ends up as undefined.
		const onClick = isCurrent ? null : (() => setFrame(clickTarget));
		elements.unshift(<Button key={i++} color="info" disabled={isCurrent} onClick={onClick}>
			{isRoot ? 'Root frame' : isCurrent ? 'This frame' : shortenName(cursor.url)}
		</Button>);
		if (!isRoot) elements.unshift(' ⟶ ');
		cursor = cursor.parentFrame;
	}
	return elements;
}


/** For the sub-frames list: wrap default SubFrameInfo in a Bootstrap column */
function SubFrameCol(props) {
	return <Col xs="6" sm="3"><SubFrameInfo {...props} /></Col>;
}


/**
 * Displays one page analysis & provides functionality for navigating into sub-frames.
 * @param {object} p
 * @param {PageManifest} p.manifest The base PageManifest to view & navigate through
 * @returns {JSX.Element}
 * */
function PageManifest({manifest: rootManifest}) {
	const [manifest, setManifest] = useState(rootManifest); // Sub-manifest currently in focus, if any

	return <Card className="mb-3">
		<CardHeader>
			Analysis for <FriendlyURL url={rootManifest.url} />
		</CardHeader>
		<Container className="card-body">
			<Row className="overview mb-3">
				<PageOverview manifest={manifest} />
				<TypeBreakdown manifest={manifest} />
			</Row>
			<Row className="sub-frames mb-3">
				<Col xs="12" className="mb-2">
					<h4 className="d-inline">Frame Hierarchy: </h4>
					<FrameBreadcrumbs frame={manifest} setFrame={setManifest} />
				</Col>
				<FrameNavigator manifest={manifest} setFrame={setManifest} FrameComponent={SubFrameCol} />
			</Row>
			<TransferTable manifest={manifest} />
		</Container>
	</Card>;
}

/** Put the last analysed object - tag-ID, URL, HTML string, or upload URL (TODO) - in the window's URL parameters. */
const setUrlVals = ({tag, url, html, upload}) => {
	DataStore.setUrlValue('tagId', tag?.id);
	DataStore.setUrlValue('url', url);
	DataStore.setUrlValue('html', html);
	DataStore.setUrlValue('upload', upload);
};


/**
 * Prompt to analyse - either GreenTag or raw URL/HTML/file.
 * Pulls from standard locations in DataStore, so needs no props.
 * @returns {JSX.Element}
 */
function AnalysePrompt() {
	const [analysisState, setAnalysisState] = useState();
	const { url, html, tagId } = DataStore.getValue(path) || {};

	const pvTag = tagId && getDataItem({ type: C.TYPES.GreenTag, id: tagId, swallow: true});
	const tag = pvTag?.value;
	const pvSavedManifest = fetchSavedManifest(tag);

	// Call MeasureServlet and analyse the specified page
	const doIt = (tagUrlHtml) => {
		setAnalysisState('loading');
		setUrlVals(tagUrlHtml);
		startAnalysis(tagUrlHtml).then(
			() => setAnalysisState('ready'),
			error => setAnalysisState({error})
		);
	};

	// Special case for uploading - instead of a Promise when starting the action,
	// we only get a callback when upload and analysis are complete.
	const onDrop = () => {
		setAnalysisState('loading');
	};
	const onUpload = res => {
		receiveUploadAnalysis(res);
		setAnalysisState('ready');
	};

	if (pvTag && !pvTag.resolved) return <Misc.Loading text="Fetching Green Ad Tag..." />;

	const loading = analysisState === 'loading';

	if (!tag) {
		return <>
			<Row>
				<Col xs="12" sm="4">
					<PropControl label="Measure an external URL" type="url" path={path} prop="url" disabled={loading}
						append={<Button color="primary" disabled={loading} onClick={() => doIt({url})}>Measure</Button>}
					/>
				</Col>
				<Col xs="12" sm="4">
					<PropControl label="Measure a HTML snippet" path={path} prop="html" disabled={loading}
						append={<Button color="primary" disabled={loading} onClick={() => doIt({html})}>Measure</Button>}
					/>
				</Col>
				<Col xs="12" sm="4">
					<FormGroup>
						<Label>Measure a page or pages in a .zip file</Label>
						<BlindUpload endpoint={ServerIO.MEASURE_ENDPOINT} label="Drop a .zip file here" disabled={loading}
							onDrop={onDrop} onUpload={onUpload} className="form-control"
						/>
					</FormGroup>
				</Col>
			</Row>
			{loading && <Row>
				<Col xs="12"><Misc.Loading text="Measuring..." /></Col>
			</Row>}
		</>;
	}

	const manifest = pvSavedManifest.value?.[0];

	// Waiting, error, or ready to (re-)start?
	let prompt;
	if (analysisState === 'loading') {
		prompt = <Misc.Loading text="Measuring..." />;
	} else if (analysisState?.error) {
		prompt = <>
			Analysis failed: {analysisState.error}.
			<Button className="ml-2" onClick={() => doIt({tag})}>Retry</Button>
		</>;
	} else {
		prompt = <>
			{!manifest && <>This Green Ad Tag creative has not yet been analysed.</>}
			{manifest && ((!manifest.timestamp) ? <>Previously analysed.</> : (
				<>Last analysed <Misc.RelativeDate date={new Date(manifest.timestamp)} />.</>
			))}
			<Button className="ml-2" onClick={() => doIt({tag})}>{manifest ? 'Re-Analyse' : 'Analyse Now'}</Button>
		</>;
	}

	return <Row className="mb-4">
		<Col xs="6">
			Green Ad Tag: {tag.name}
		</Col>
		<Col xs="6">
			{prompt}
		</Col>
	</Row>;
}


function MeasurePage() {
	const { url, html, tagId } = DataStore.getValue(path) || {};

	// Check page URL for items to pre-fill fields
	useEffect(() => {
		['url', 'html', 'tagId'].forEach(key => {
			const val = DataStore.getUrlValue(key);
			if (val) DataStore.setValue([...path, key], val);
		});
	}, []);

	const tag = tagId && getDataItem({ type: C.TYPES.GreenTag, id: tagId, swallow: true})?.value;
	const smPath = savedManifestPath({tag, url, html});
	const results = DataStore.getValue(smPath);

	return <>
		<h1>Measurement Tool</h1>
		<Container>
			<AnalysePrompt />
			{results ? <Row>
				<Col xs="12">
					{results.map(r => (
						<PageManifest key={r.url} manifest={r} />
					))}
				</Col>
			</Row> : null}
		</Container>
	</>;
}


export default MeasurePage;
